/**
 * AgentGreetingModal — "Mensagem de chegada" (transferência) e, atrás da flag
 * agent_resume_greeting, "Mensagem de retomada" (banner "Retomar agente") de
 * UM agente.
 *
 * Modos: off | fixa | gerada por IA. Compartilhado entre a tela de Agentes e o
 * drawer "Agente do número" (Membros) para padronizar a configuração.
 * Carrega/salva via /agents/{id}/transfer-greeting e /agents/{id}/resume-greeting.
 *
 * Props: agent ({id, name}), colors, onClose(saved:boolean).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../services/global/api';
import useFeatureFlags from '../../hooks/useFeatureFlags';

const MODES = [
  ['off', 'Não enviar nada', 'Responde só quando o contato mandar a próxima mensagem.'],
  ['fixed', 'Mensagem fixa', 'Envia o texto abaixo ao receber a transferência.'],
  ['generated', 'Gerada por IA', 'O agente gera a apresentação com o motivo, resumo de passagem, campos e comentários.'],
];

// Retomada pelo banner: o atendente humano devolve a conversa ao agente.
const RESUME_MODES = [
  ['off', 'Não enviar nada', 'O agente só volta a responder na próxima mensagem do contato.'],
  ['fixed', 'Mensagem fixa', 'Envia o texto abaixo assim que o atendente clicar em "Retomar agente".'],
  ['generated', 'Gerada por IA', 'O agente escreve um aviso curto de que vai dar seguimento ao atendimento.'],
];

// Presets do respiro entre a transferência e a chegada. 0 = comportamento
// histórico (sai no mesmo instante e soa robô quando vem logo após o
// "vou te passar para..." do agente anterior).
const DELAYS = [
  [0, 'Imediato'],
  [30, '30s'],
  [60, '1 min'],
  [120, '2 min'],
  [300, '5 min'],
];

const ModeList = ({ modes, value, onChange, colors }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {modes.map(([v, label, desc]) => (
      <button
        key={v}
        onClick={() => onChange(v)}
        style={{
          textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
          backgroundColor: value === v ? `${colors.accentPrimary}10` : 'transparent',
          border: `1.5px solid ${value === v ? colors.accentPrimary : colors.borderColor}`,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: value === v ? colors.accentPrimary : colors.textPrimary }}>
          {label}
        </div>
        <div style={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>{desc}</div>
      </button>
    ))}
  </div>
);

const DelayPicker = ({ value, onChange, colors, title, hint }) => (
  <div style={{ marginTop: 4 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary, marginBottom: 6 }}>
      {title}
    </div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {DELAYS.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          style={{
            padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
            backgroundColor: value === v ? `${colors.accentPrimary}10` : 'transparent',
            border: `1.5px solid ${value === v ? colors.accentPrimary : colors.borderColor}`,
            color: value === v ? colors.accentPrimary : colors.textSecondary,
          }}
        >
          {label}
        </button>
      ))}
    </div>
    <div style={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.4, marginTop: 6 }}>
      {hint}
    </div>
  </div>
);

const inputStyle = (colors) => ({
  width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8,
  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
  color: colors.textPrimary, fontSize: 13, outline: 'none', fontFamily: 'inherit',
});

const AgentGreetingModal = ({ agent, colors, onClose }) => {
  const [mode, setMode] = useState('off');
  const [text, setText] = useState('');
  const [delaySeconds, setDelaySeconds] = useState(0);
  const [busy, setBusy] = useState(false);

  // Retomada (flag agent_resume_greeting): estado próprio, salvo à parte.
  const { flags: featureFlags } = useFeatureFlags();
  const resumeEnabled = Boolean(featureFlags?.agent_resume_greeting);
  const [resumeMode, setResumeMode] = useState('off');
  const [resumeText, setResumeText] = useState('');
  const [resumeDelay, setResumeDelay] = useState(0);
  const [resumeTemplateName, setResumeTemplateName] = useState('');
  const [resumeTemplateLanguage, setResumeTemplateLanguage] = useState('pt_BR');

  const load = useCallback(async () => {
    if (!agent) return;
    try {
      const r = await api.get(`/agents/${agent.id}/transfer-greeting`);
      const g = r.data?.data?.transfer_greeting || {};
      setMode(g.mode || 'off');
      setText(g.text || '');
      setDelaySeconds(Number(g.delay_seconds) || 0);
    } catch (err) {
      console.warn('[AgentGreetingModal] load:', err);
    }
    if (!resumeEnabled) return;
    try {
      const r = await api.get(`/agents/${agent.id}/resume-greeting`);
      const g = r.data?.data?.resume_greeting || {};
      setResumeMode(g.mode || 'off');
      setResumeText(g.text || '');
      setResumeDelay(Number(g.delay_seconds) || 0);
      setResumeTemplateName(g.template_name || '');
      setResumeTemplateLanguage(g.template_language || 'pt_BR');
    } catch (err) {
      console.warn('[AgentGreetingModal] load resume:', err);
    }
  }, [agent, resumeEnabled]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!agent) return;
    setBusy(true);
    try {
      await api.put(`/agents/${agent.id}/transfer-greeting`, {
        mode,
        text: mode === 'fixed' ? text : null,
        delay_seconds: delaySeconds,
      });
      if (resumeEnabled) {
        await api.put(`/agents/${agent.id}/resume-greeting`, {
          mode: resumeMode,
          text: resumeMode === 'fixed' ? resumeText : null,
          delay_seconds: resumeDelay,
          template_name: resumeMode === 'off' ? null : (resumeTemplateName.trim() || null),
          template_language: resumeMode === 'off' ? null : (resumeTemplateLanguage.trim() || 'pt_BR'),
        });
      }
      onClose?.(true);
    } catch (err) {
      console.error('[AgentGreetingModal] save:', err);
    } finally {
      setBusy(false);
    }
  };

  if (!agent) return null;

  const saveDisabled = busy
    || (mode === 'fixed' && !text.trim())
    || (resumeEnabled && resumeMode === 'fixed' && !resumeText.trim());

  return (
    <div
      onClick={() => onClose?.(false)}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 2100, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460, maxHeight: '92vh', overflowY: 'auto',
          backgroundColor: colors.bgPrimary,
          borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: '20px',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: colors.textPrimary, marginBottom: 4 }}>
          Mensagem de chegada — {agent.name}
        </div>
        <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5, marginBottom: 14 }}>
          O que este agente faz ao RECEBER um contato transferido (de outro agente ou de um humano).
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ModeList modes={MODES} value={mode} onChange={setMode} colors={colors} />
          {mode === 'fixed' && (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ex.: Olá! Sou a Ana, vou continuar seu atendimento a partir de agora."
              style={{ ...inputStyle(colors), resize: 'vertical' }}
            />
          )}
          {mode !== 'off' && (
            <DelayPicker
              value={delaySeconds}
              onChange={setDelaySeconds}
              colors={colors}
              title="Aguardar antes da mensagem de chegada"
              hint={'Um respiro entre o "vou te transferir" e a chegada soa mais humano. Se um humano assumir ou o agente já tiver respondido durante a espera, a mensagem de chegada não é enviada.'}
            />
          )}
        </div>

        {resumeEnabled && (
          <>
            <div style={{ borderTop: `1px solid ${colors.borderColor}`, margin: '18px 0 14px' }} />
            <div style={{ fontSize: 15, fontWeight: 600, color: colors.textPrimary, marginBottom: 4 }}>
              Mensagem de retomada
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5, marginBottom: 12 }}>
              O que este agente envia quando um atendente devolve a conversa a ele pelo
              botão "Retomar agente" do banner.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ModeList modes={RESUME_MODES} value={resumeMode} onChange={setResumeMode} colors={colors} />
              {resumeMode === 'fixed' && (
                <textarea
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Ex.: Obrigado por aguardar. A partir de agora vou dar seguimento ao seu atendimento."
                  style={{ ...inputStyle(colors), resize: 'vertical' }}
                />
              )}
              {resumeMode !== 'off' && (
                <>
                  <DelayPicker
                    value={resumeDelay}
                    onChange={setResumeDelay}
                    colors={colors}
                    title="Aguardar antes da mensagem de retomada"
                    hint="Se um humano assumir de novo, o agente for pausado ou alguém já tiver respondido durante a espera, a mensagem não é enviada."
                  />
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary, marginBottom: 6 }}>
                      WhatsApp Business fora da janela de 24 h
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        value={resumeTemplateName}
                        onChange={(e) => setResumeTemplateName(e.target.value)}
                        placeholder="Nome do template aprovado (opcional)"
                        style={{ ...inputStyle(colors), flex: 1 }}
                      />
                      <input
                        value={resumeTemplateLanguage}
                        onChange={(e) => setResumeTemplateLanguage(e.target.value)}
                        placeholder="pt_BR"
                        style={{ ...inputStyle(colors), width: 90 }}
                      />
                    </div>
                    <div style={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.4, marginTop: 6 }}>
                      Fora da janela de 24 h a Meta só aceita template aprovado: com um nome
                      aqui, é ele que sai; sem template, a retomada não é enviada. Instagram e
                      Messenger fora da janela não recebem nada (regra do canal).
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => onClose?.(false)}
            style={{
              padding: '8px 16px', borderRadius: 8, fontSize: 13,
              border: `1px solid ${colors.borderColor}`, backgroundColor: 'transparent',
              color: colors.textSecondary, cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saveDisabled}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px',
              borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none',
              backgroundColor: colors.accentPrimary, color: '#fff',
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.75 : 1,
            }}
          >
            {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentGreetingModal;
